import { CoObjectParameters, DimensionDetails } from "../../interfaces/CoObject";
import { DocumentParameters } from "../../document/ParametersDocument";
import { fetchReferringScenarios } from "../../../api/object/Parameters";
import { ReferringScenarios } from "../../../api/models/ReferringScenarios";
import NotificationManager from "../../../common/notification/NotificationManager";
import { formatMessage } from "../../../utils/helpers/LanguageHelper";

export const isScenarioMetadata = (dimensionDetails: DimensionDetails) => dimensionDetails?.structureCode === "SCE";

export const getReferrableScenarios = (inheritableParameters: CoObjectParameters | DocumentParameters[]) => {
  const referrableScenarios = (inheritableParameters as CoObjectParameters)?.filter(
    (p) => isScenarioMetadata(p) && p.values.length === 1
  );
  if (referrableScenarios?.length !== 1) return null;
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  return referrableScenarios[0];
};

export const getReferringScenarios = async (
  inheritableParametersInThisDb: CoObjectParameters | DocumentParameters[],
  dbId: string
) => {
  const referrableScenario = getReferrableScenarios(inheritableParametersInThisDb);
  if (!referrableScenario) return null;
  // eslint-disable-next-line no-undef
  let referringScenarios: ReferringScenarios;

  const scenarioCode = referrableScenario.values[0].code;
  if (scenarioCode) referringScenarios = await fetchReferringScenarios(dbId, scenarioCode);

  return referringScenarios;
};

export const getUpdatedReferredScenario = (
  item: DimensionDetails,
  thisDimensionsInheritableParameters: DimensionDetails[],
  referringScenario: ReferringScenarios | null // referringScenarios might be null in case of a multiobj update (IDS-1100)
): { newScenarios: CoObjectParameters; isRefChanged: boolean } => {
  // following const contains the inheritable scenario only if it is referable (exactly 1 value)
  const inheritableReferrableScenario = getReferrableScenarios(thisDimensionsInheritableParameters);
  const isRefStillPresent = referringScenario?.referenceScenarios.some((ref) => ref.reference === item.values[0].ref);
  if ((isRefStillPresent || !referringScenario) && inheritableReferrableScenario)
    // !referringScenario covers the case of the multiobj update,
    // that should leave the old ref with the new inheritableParams even if ref is no longer present

    return {
      newScenarios: [
        {
          ...inheritableReferrableScenario,
          code: item.code,
          inherited: true,
          values: [
            {
              ...inheritableReferrableScenario.values[0],
              ref: item.values[0].ref,
              code: referringScenario?.code ?? inheritableReferrableScenario.values[0].code,
            },
          ],
        },
      ],
      isRefChanged: false,
    };

  if (inheritableReferrableScenario) {
    //if the fresh inheritable scenario is present, but the selected ref is no longer present
    NotificationManager.addNotification({
      key: "unavailable.error",
      title: "",
      message: formatMessage("cof.createnewobject.referringscenario.unavailable.error"),
      type: "error",
      isTemporary: false,
    });
  }
  // the previously inherited scenario is no longer referable (!=1 values)
  else
    NotificationManager.addNotification({
      key: "unreferable.error",
      title: "",
      message: formatMessage("cof.createnewobject.referringscenario.unreferable.error"),
      type: "error",
      isTemporary: false,
    });
  const newRef = referringScenario ? null : item.values[0].ref;
  const newScenarios = thisDimensionsInheritableParameters.map((p) => ({
    ...p,
    code: item.code,
    inherited: true,
    values: p.values.map((v) => ({ ...v, ref: newRef })),
  }));
  return { newScenarios, isRefChanged: true };
};
